import { REQUIRED_MESSAGES } from '@srm/ui/lib/requiredMessage';
import React, { useEffect, useState } from 'react';
import { IOption } from '@srm/ui/types/IOption';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { DictionariesService } from '@services/DictionariesService';
import { AttachActionsService, IAttachPropertyField } from '@services/actions/AttachActionsService';
import InputControl from '@srm/ui/controls/InputControl';
import SelectControl from '@srm/ui/controls/SelectControl';
import Modal from '@srm/ui/modal';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import ModalLoadingOverlay from '@srm/ui/modal-loading-overlay';
import Hint from '@srm/ui/hint';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';
import { requiredTextRules } from '@srm/ui/lib/requiredTextRules';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { IEditAttachProps } from './IEditAttachProps';

// Обязательность свойства в конфигурации потока (Flows[0].Property) задаётся
// атрибутом `required` — тем же, что и для полей форм в схеме платформы
// (FormTemplate.BaseField → XmlAttribute "required"); XML→JSON отдаёт его как
// строковый `@required`. В текущих конфигах Русагро/Акрон attach-edit его пока
// никто не выставляет, поэтому валидация неактивна, но готова к появлению атрибута.
const isPropertyRequired = (property: any): boolean => {
    const value = property?.['@required'];
    return value === true || value === 'true';
};

const getFlowProperties = (options: any): any[] => {
    const properties = options.Options.Flows[0].Property;
    return (Array.isArray(properties) ? properties : [properties]).filter((property) => property);
};

const getAttachPropertyFields = (flowProperties: any[]): IAttachPropertyField[] =>
    flowProperties.map((property) => ({
        key: property['@key'],
        fieldType: property['@fieldType'] ?? 'property',
    }));

export const EditAttachModal: React.FC<IEditAttachProps> = (props: IEditAttachProps) => {
    const activated = React.useRef(false);
    const hasFlowConfig = props.options.Options.Flows[0] !== undefined;
    const [errorText, setErrorText] = useState<string>();
    // Подготовка (свойства, справочники) и сохранение — разные состояния: подготовку можно
    // бросить закрытием окна, сохранение — нет.
    const [loading, setLoading] = useState<boolean>(hasFlowConfig);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const isBusy = loading || isSubmitting;
    const [dictsData, setDictsData] = useState<{ [fieldKey: string]: IOption[] }>();
    const [readOnlyProps, setReadOnlyProps] = useState<{ [fieldKey: string]: boolean }>({});

    const editAttachmentFormState = useForm<any>({
        mode: 'onBlur', // "onChange"
    });
    const hasUnsavedInput = useUnsavedFormInput(editAttachmentFormState, {
        textFields: hasFlowConfig
            ? getFlowProperties(props.options)
                  .filter((property) => property['@valueType'] === 'text')
                  .map((property) => property['@key'])
            : [],
    });
    const close = () => props.completeHandler?.(false);

    useEffect(() => {
        activated.current = true;
        return () => {
            activated.current = false;
        };
    }, []);

    useEffect(() => {
        if (!hasFlowConfig) return;
        // Закрытое окно ответы подготовки не применяет.
        let ignore = false;
        const failLoading = (error: unknown) => {
            if (ignore) return;
            setErrorText(error as string);
            setLoading(false);
        };
        setLoading(true);

        // если множественное редактирование - используем ИД первого аттача
        const attachIdsSplit = props.attachId.split(',').filter((item) => item);
        const firstAttachId = attachIdsSplit[0];

        const flowProperties = getFlowProperties(props.options);
        const propertyFields = getAttachPropertyFields(flowProperties);
        const dictsNamesToLoad: string[] = flowProperties.map((x) => x['@dictName']).filter((x) => x);
        DictionariesService.getAllDictsItems(dictsNamesToLoad).then((dictResData) => {
            if (ignore) return;
            const service = new AttachActionsService(props.docId, '-1');
            service.getAttachProperties(propertyFields, firstAttachId).then((res) => {
                if (ignore) return;
                const newResetState: { [index: string]: any } = {};
                const newReadOnlyProps: { [index: string]: boolean } = {};

                const attachIdsSplit = props.attachId.split(',').filter((item) => item);

                for (const key in res.data) {
                    newResetState[key] = res.data[key].value ?? '';

                    if (attachIdsSplit.length == 1) {
                        newReadOnlyProps[key] = res.data[key].isReadOnly; //Только для SINGLE SELECT такое определяем. т.к. ниже мы целенаправленно смотрим по всем аттачам и корректно определяем что там readonly а что нет
                    }
                }

                const dicts: { [fieldKey: string]: IOption[] } = {};
                const dictsFields = flowProperties.filter((x) => x['@dictName']);
                dictsFields.forEach((fld) => {
                    const fldKey = fld['@key'];
                    const existingDictValue = newResetState[fldKey];

                    const dictOptions = dictResData.data[fld['@dictName']].map((x) => {
                        return {
                            value: fld['@valueColumn']
                                ? x.fields.find((f) => f.name == fld['@valueColumn'])?.value
                                : x.code,
                            label: x.code,
                        };
                    });

                    dicts[fldKey] = dictOptions;

                    //Дефолтовое значение берем из справочника. Либо же добавляем в список, если оно на нашлось
                    const defaultFormValues: IOption[] = dictOptions.filter(
                        (dictRow) => dictRow.value == existingDictValue,
                    );
                    newResetState[fldKey] =
                        defaultFormValues.length > 0
                            ? defaultFormValues
                            : [
                                  {
                                      value: res.data[fld['@key']]?.value ?? '',
                                      label: res.data[fld['@key']]?.value ?? '',
                                  } as IOption,
                              ]; //dictResData.data[fld['@dictName']].find(dictRow => dictRow.code == existingDictValue);
                });

                const nonMultiEditableFields = flowProperties.filter((x) => x['@multiEditable'] !== 'true');
                if (attachIdsSplit.length > 1) {
                    // если множественное редактирование - фильтрует по атрибуту @multiEditable
                    nonMultiEditableFields.forEach((fld) => {
                        const fldKey = fld['@key'];
                        delete newResetState[fldKey];
                    });

                    const promises: any = [];
                    promises.push(
                        service.getAttachesReadOnlyProperties(propertyFields, attachIdsSplit).then((result) => {
                            result.data.forEach((key) => {
                                newReadOnlyProps[key] = true;
                            });
                        }),
                    );

                    Promise.all(promises).then(() => {
                        // readOnly поля тоже исключаются из группового редактирования
                        for (const k in newReadOnlyProps) {
                            if (newReadOnlyProps[k]) delete newResetState[k];
                        }

                        if (activated.current && !ignore) {
                            setDictsData(dicts);
                            setReadOnlyProps(newReadOnlyProps);

                            editAttachmentFormState.reset({});
                            editAttachmentFormState.reset(newResetState);
                            setLoading(false);
                        }
                    }, failLoading);
                } else {
                    if (activated.current && !ignore) {
                        setDictsData(dicts);
                        setReadOnlyProps(newReadOnlyProps);

                        editAttachmentFormState.reset({});
                        editAttachmentFormState.reset(newResetState);
                        setLoading(false);
                    }
                }
            }, failLoading);
        }, failLoading);
        return () => {
            ignore = true;
        };
    }, [editAttachmentFormState]);

    const renderControl = (property: any, fileFields: any) => {
        {
            const required = isPropertyRequired(property);
            switch (property['@valueType']) {
                case 'text':
                    return (
                        <InputControl
                            label={property['@displayName']}
                            placeholder={property['@displayName']}
                            name={property['@key']}
                            readOnly={readOnlyProps[property['@key']]}
                            required={required}
                            rules={required ? requiredTextRules() : undefined}
                            control={editAttachmentFormState.control}
                            value={fileFields[property['@key']]}
                        />
                    );
                case 'dict':
                    if (!dictsData) return <></>;
                    return (
                        <SelectControl
                            options={dictsData[property['@key']]}
                            multiselect={false}
                            name={property['@key']}
                            control={editAttachmentFormState.control}
                            label={property['@displayName']}
                            onlySelect={true}
                            required={required}
                            rules={
                                required
                                    ? { validate: (value: any) => (Array.isArray(value) && value.length > 0) || REQUIRED_MESSAGES.select }
                                    : undefined
                            }
                            disabled={readOnlyProps[property['@key']]}
                            highlightMenuItems={false}
                        />
                    );
            }
        }
    };

    const onSubmit: SubmitHandler<any> = (formValues: any) => {
        setErrorText(undefined);
        setIsSubmitting(true);

        // не мутируем значения формы напрямую: при ошибке сервиса модалка
        // остаётся открытой, а dict-поля должны сохранить вид IOption[]
        const attachProperties: { [key: string]: any } = { ...formValues };
        for (const k in attachProperties) {
            if (Array.isArray(attachProperties[k]) && attachProperties[k].length > 0) {
                attachProperties[k] = attachProperties[k][0].value;
            }
        }

        const flowProperties = getFlowProperties(props.options);
        const propertyFields = getAttachPropertyFields(flowProperties);
        const service = new AttachActionsService(props.docId, '-1');
        return service
            .editAttach(attachProperties, props.attachId, propertyFields)
            .then(() => {
                if (props.completeHandler) props.completeHandler(true);
            })
            .catch((error) => setErrorText(error))
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <Modal
            className={clsx('modal-dlg-container')}
            size={props.modalSize}
            onClose={close}
            hasUnsavedInput={hasUnsavedInput}
            closeDisabled={isSubmitting}
            header="Редактирование свойств присоединенного файла"
            footer={
                <div className="modal-dlg-buttons">
                    <Button
                        size="sm"
                        aria-label={props.okButtonText}
                        onClick={editAttachmentFormState.handleSubmit(onSubmit)}
                        disabled={isBusy || !hasFlowConfig}
                    >
                        {props.okButtonText}
                    </Button>
                    <Button
                        variant="light"
                        size="sm"
                        aria-label={props.cancelButtonText}
                        onClick={close}
                        disabled={isSubmitting}
                    >
                        {props.cancelButtonText}
                    </Button>
                </div>
            }
        >
            <ModalLoadingOverlay visible={isBusy}>
                <div>
                    {errorText && <Hint role="alert" title={`Ошибка: ${errorText}`} tone="danger" maxWidth="100%" />}
                    {Array.isArray(props.rowData) && props.rowData.length > 1 ? (
                        <Hint
                            title={`Свойства будут изменены у всех выбранных файлов!`}
                            tone="warning"
                            maxWidth="100%"
                        />
                    ) : (
                        <></>
                    )}
                    {!hasFlowConfig ? (
                        <Hint
                            title={`Ошибка: не удалось получить конфигурацию действия для данного потока.`}
                            tone="danger"
                            maxWidth="100%"
                        />
                    ) : (
                        <FormProviderControl
                            formMethods={editAttachmentFormState}
                            onSubmit={onSubmit}
                            ignoreEnterSubmit={true}
                        >
                            <HookFormProvider controller={Controller}>
                                <div>
                                    {(Array.isArray(props.options.Options.Flows[0].Property)
                                        ? props.options.Options.Flows[0].Property
                                        : [props.options.Options.Flows[0].Property]
                                    )
                                        // если множественное редактирование - фильтрует по атрибуту @multiEditable
                                        .filter((prop: any) => {
                                            return Array.isArray(props.rowData) && props.rowData.length > 1
                                                ? prop['@multiEditable'] === 'true' && !readOnlyProps[prop['@key']]
                                                : true;
                                        })
                                        .map((property: any, index: number) => (
                                            <div key={index}>
                                                {Array.isArray(props.rowData)
                                                    ? renderControl(property, props.rowData[0].fields)
                                                    : renderControl(property, props.rowData.fields)}
                                            </div>
                                        ))}
                                </div>
                            </HookFormProvider>
                        </FormProviderControl>
                    )}
                </div>
            </ModalLoadingOverlay>
        </Modal>
    );
};
